import React, { useEffect, useState } from 'react';
import { Box, Grid2 } from '@mui/material';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import CustomTypography from '@ui-components/Typography/CustomTypography';
import CustomIconButton from '@ui-components/IconButton/CustomIconButton';
import CustomDivider from '@ui-components/Divider/CustomDivider';
import CustomTextField from '@ui-components/TextField/CustomTextField';
import CustomButton from '@ui-components/Button/CustomButton';

import { useLocation, useNavigate, useParams } from 'react-router-dom';
import BackIcon from '@assets/images/BackIcon.svg';
import {
  CustomAutoComplete,
  CustomDatePicker,
  CustomSwitch,
} from '@qler-view/shared_lib/src';
import CustomSelect from '@ui-components/Select/CustomSelect';
import {
  useGetPartnerMetaData,
  useSaveOrUpdatePartner,
  useGetPartnerDetailsById,
} from '../../API/hooks/PartnerHook';
import type { ServicesProvided } from '../../API/services/PartnerService';
import type { Option } from '@ui-components/AutoComplete/CustomAutoComplete';
import {
  PARTNER_ROUTE_PATH,
  VIEW_PARTNER_ROUTE_PATH,
} from '../../routers/routePaths';
import moment from 'moment';
import formatPhoneNumber from '../components/FormatPhoneNumber';
import validatePostalCode from '../components/ValidatePostalCode';
import ConfirmDiscardDialog from '../components/ConfirmDiscardDialog';

const partnerInitialValues = {
  partnerInfo: {
    id: 0,
    name: '',
    partnerBusinessId: '',
    servicesProvided: [] as ServicesProvided[],
    alias: '',
    partnerTypeId: 0,
    website: '',
    activePartnerSince: '',
    terminationDate: '',
    assignedTo: 0,
    status: true,
  },

  contactDetails: {
    id: 0,
    primaryPhone: '',
    itHelpDeskEmail: '',
    itHelpDeskPhone: '',
    fax: '',
    contactEmail: '',
    otherEmail: '',
    faxDescription: '',
    otherEmailDescription: '',
  },

  itSystemDetails: {
    id: 0,
    networkAccessType: '',
    telemedicinePlatform: '',
    networkAccessUrl: '',
    telemedicinePlatformUrl: '',
    vpnGatewaySoftware: '',
    trainingEquipment: '',
    emr: '',
    emailDomainPrimary: '',
    documentationIn: '',
    emailDomainSecondary: '',
    mfa: '',
    emailDomainTertiary: '',
    devicesUtilized: '',
    itNotes: '',
  },

  address: {
    id: 0,
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '0',
    postalCode: '',
    county: '',
  },

  description: '',
};

const AddNewPartner: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { partnerId } = useParams<{ partnerId?: string }>();

  const userId = localStorage.getItem('userId') ?? '0';
  const [partnerSubmitAction, setPartnerSubmitAction] = useState<string>('');
  const [openPartnerConfirmDialog, setOpenPartnerConfirmDialog] =
    useState(false);

  const { PartnerMetaData } = useGetPartnerMetaData(userId);

  const onSuccess = () => {
    if (partnerId) {
      navigate(`${PARTNER_ROUTE_PATH}?toast=update`);
    } else {
      navigate(`${PARTNER_ROUTE_PATH}?toast=success`);
    }
  };

  const { mutate: saveOrUpdatePartner } = useSaveOrUpdatePartner(onSuccess);

  const handlePartnerSaveOrUpdate = async (
    partnerData: typeof partnerInitialValues
  ) => {
    const { ...payload } = partnerData;

    if (partnerSubmitAction) {
      saveOrUpdatePartner({ userId, action: partnerSubmitAction, ...payload });
    }
  };
  const handleOpenPartnerConfirmDialog = (state: boolean) => {
    setOpenPartnerConfirmDialog(state);
  };

  const { PartnerDetailsById, isLoading } = useGetPartnerDetailsById(
    userId,
    Number(partnerId)
  );

  const [initialValues, setInitialValues] = useState(partnerInitialValues);

  useEffect(() => {
    if (PartnerDetailsById) {
      setInitialValues({
        partnerInfo: PartnerDetailsById.partnerInfo,
        contactDetails: PartnerDetailsById.contactDetails,
        itSystemDetails: PartnerDetailsById.itSystemDetails,
        address: PartnerDetailsById.address,
        description: PartnerDetailsById.description,
      });
    }
  }, [PartnerDetailsById]);

  const mapUsStatesToOptions = (states: string[] | undefined): Option[] => {
    return (
      states?.map((state) => ({
        name: state,
        id: state,
      })) ?? []
    );
  };

  const partnerValidationSchema = Yup.object({
    // REQUIRED fields
    partnerInfo: Yup.object().shape({
      name: Yup.string().required('Partner Name is required'),
      assignedTo: Yup.number()
        // .typeError('Assigned To must be a number')
        .required('Assigned To is required')
        .min(1, 'Assigned To is required'),
    }),
    // OPTIONAL but validate if value exists
    contactDetails: Yup.object().shape({
      primaryPhone: Yup.string()
        .nullable()
        .test(
          'valid-primaryPhone',
          'Phone must be in format (123) 456-7890',
          (value) => !value || /^\(\d{3}\)\s\d{3}-\d{4}$/.test(value)
        ),

      itHelpDeskEmail: Yup.string().nullable().email('Invalid Email'),
      itHelpDeskPhone: Yup.string()
        .nullable()
        .test(
          'valid-itHealpDeskPhone',
          'Phone must be in format (123) 456-7890',
          (value) => !value || /^\(\d{3}\)\s\d{3}-\d{4}$/.test(value)
        ),

      contactEmail: Yup.string().nullable().email('Invalid Email'),

      fax: Yup.string()
        .nullable()
        .test(
          'valid-fax',
          'Phone must be in format (123) 456-7890',
          (value) => !value || /^\(\d{3}\)\s\d{3}-\d{4}$/.test(value)
        ),

      otherEmail: Yup.string().nullable().email('Invalid Email'),

      faxDescription: Yup.string().max(
        500,
        'Description must be no more than 500 characters long.'
      ),
      otherEmailDescription: Yup.string().max(
        500,
        'Description must be no more than 500 characters long.'
      ),
    }),
    itSystemDetails: Yup.object().shape({
      devicesUtilized: Yup.string().max(
        1500,
        'Devices Utilized must be no more than 1500 characters long.'
      ),
      itNotes: Yup.string().max(
        500,
        'IT Notes must be no more than 500 characters long.'
      ),
    }),

    address: Yup.object().shape({
      postalCode: Yup.string()
        .transform((value) => (value === '0' || value === 0 ? null : value))
        .nullable()
        .test(
          'valid-postalCode',
          'PostalCode must be exactly 5 digits',
          (value) => !value || /^\d{5}$/.test(value)
        ),
    }),

    description: Yup.string().max(
      1000,
      'Description must be no more than 1000 characters long.'
    ),
  });

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CustomTypography
          id="loading-text"
          variant="h5"
          className="text-align-center pt-32"
          color="textDisabled"
        >
          Loading...
        </CustomTypography>
      </Box>
    );
  }

  const handleBackButtonClick = () => {
    if (partnerId && !location.state?.isList) {
      navigate(VIEW_PARTNER_ROUTE_PATH + `/${partnerId}`, {
        replace: true,
      });
    } else {
      navigate(PARTNER_ROUTE_PATH);
    }
  };

  return (
    <Box>
      <Box className="pt-20 pb-20">
        <Grid2 container>
          <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
            <Box className="text-align-left display-flex">
              <CustomIconButton
                id="back-button"
                icon={<BackIcon />}
                onClick={() => {
                  handleBackButtonClick();
                }}
              ></CustomIconButton>

              <CustomTypography
                className="pl-8"
                variant="h4"
                id="add-new-partner-text"
              >
                {partnerId ? 'Edit Partner' : 'Add New Partner'}
              </CustomTypography>
              <CustomTypography
                className="pl-12"
                variant="h4"
                id="partner-business-id"
              >
                {partnerId
                  ? `(${initialValues.partnerInfo.partnerBusinessId})`
                  : ''}
              </CustomTypography>
            </Box>
          </Grid2>
        </Grid2>
      </Box>
      <Formik
        initialValues={initialValues}
        validateOnMount={false}
        validationSchema={partnerValidationSchema}
        onSubmit={(values) => {
          handlePartnerSaveOrUpdate(values);
        }}
        enableReinitialize
      >
        {({
          values,
          errors,
          touched,
          // isValid,
          // dirty,
          handleSubmit,
          handleBlur,
          setFieldValue,
        }) => (
          <Form>
            <Box className="form-background p-24">
              <Grid2 container>
                <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                  <CustomTypography
                    variant="h5"
                    className="text-align-left pb-32"
                    id="facility-info-text"
                  >
                    Partner Information
                  </CustomTypography>
                </Grid2>
              </Grid2>
              <Box className="display-flex">
                <Grid2 container spacing={4} className="width-80">
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="partner-name-label"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left pb-12"
                    >
                      Partner Name&nbsp;<span className="color-red">*</span>
                    </CustomTypography>
                    <CustomTextField
                      id="txt-partner-name"
                      name="partnerName"
                      placeholder="Type Partner Name"
                      size="small"
                      value={values.partnerInfo.name}
                      onChange={(event) => {
                        setFieldValue('partnerInfo.name', event.target.value);
                      }}
                    ></CustomTextField>
                    {errors?.partnerInfo?.name &&
                      touched?.partnerInfo?.name && (
                        <CustomTypography
                          id="validate-diagnosis"
                          color="error"
                          variant="body3"
                          className="pt-8 text-align-left"
                        >
                          {errors.partnerInfo.name}
                        </CustomTypography>
                      )}
                  </Grid2>

                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="alias-label"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left pb-12"
                    >
                      Alias
                    </CustomTypography>
                    <CustomTextField
                      id="txt-alias"
                      placeholder="Type alias Name"
                      size="small"
                      value={values.partnerInfo.alias}
                      onChange={(event) => {
                        setFieldValue('partnerInfo.alias', event.target.value);
                      }}
                    ></CustomTextField>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="status-label"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left pb-12"
                    >
                      Status
                    </CustomTypography>
                    <Box className="text-align-left">
                      <CustomSwitch
                        id="status-switch"
                        labelPlacement="end"
                        checked={values.partnerInfo.status}
                        onChange={(event) => {
                          setFieldValue(
                            'partnerInfo.status',
                            event.target.checked
                          );
                        }}
                        label={
                          values.partnerInfo.status ? 'Active' : 'Inactive'
                        }
                      />
                    </Box>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="type-label"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left pb-12"
                    >
                      Type
                    </CustomTypography>
                    <CustomSelect
                      id="type-select"
                      placeholder="Select Type"
                      size="small"
                      onChange={(value) => {
                        setFieldValue(
                          'partnerInfo.partnerTypeId',
                          Number(value)
                        );
                      }}
                      selectedValue={values.partnerInfo.partnerTypeId}
                      options={PartnerMetaData?.partnerTypes ?? []}
                      fullWidth
                    ></CustomSelect>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="website-label"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left pb-12"
                    >
                      Website
                    </CustomTypography>
                    <CustomTextField
                      id="txt-website"
                      placeholder="Type Website Name"
                      size="small"
                      value={values.partnerInfo.website}
                      onChange={(event) => {
                        setFieldValue(
                          'partnerInfo.website',
                          event.target.value
                        );
                      }}
                    ></CustomTextField>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="services-provided-label"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left pb-12"
                    >
                      Services Provided
                    </CustomTypography>
                    <CustomAutoComplete
                      id="services-provided"
                      placeholder="Select Services Provided"
                      size="small"
                      options={PartnerMetaData?.servicesProvided ?? []}
                      value={values.partnerInfo.servicesProvided}
                      onChange={(selectedOptions) => {
                        setFieldValue(
                          'partnerInfo.servicesProvided',
                          selectedOptions
                        );
                      }}
                      disableCloseOnSelect={true}
                      limitTags={2}
                      noOptionsText="No Servidces Provided Found"
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="active-since-label"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left pb-12"
                    >
                      Active Partner Since
                    </CustomTypography>
                    <CustomDatePicker
                      id="active-since-date-picker"
                      format="MM-DD-YYYY"
                      value={moment(values.partnerInfo.activePartnerSince)}
                      onChange={(date) => {
                        setFieldValue(
                          'partnerInfo.activePartnerSince',
                          date ? date.format('MM-DD-YYYY') : ''
                        );
                      }}
                    ></CustomDatePicker>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="termination-label"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left pb-12"
                    >
                      Termination Date
                    </CustomTypography>
                    <CustomDatePicker
                      id="termination-date-picker"
                      format="MM-DD-YYYY"
                      value={moment(values.partnerInfo.terminationDate)}
                      onChange={(date) => {
                        setFieldValue(
                          'partnerInfo.terminationDate',
                          date ? date.format('MM-DD-YYYY') : ''
                        );
                      }}
                    ></CustomDatePicker>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="assigned-to-label"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left pb-12"
                    >
                      Assigned To&nbsp;<span className="color-red">*</span>
                    </CustomTypography>
                    <CustomSelect
                      id="assigned-to-select"
                      placeholder="Select Assigned To"
                      size="small"
                      onChange={(value) => {
                        setFieldValue('partnerInfo.assignedTo', Number(value));
                      }}
                      selectedValue={values.partnerInfo.assignedTo}
                      options={PartnerMetaData?.assignedTo ?? []}
                      fullWidth
                      emptyOptionLabel="No Assignees"
                      displayEmpty
                    />
                    {errors?.partnerInfo?.assignedTo &&
                      touched?.partnerInfo?.assignedTo && (
                        <CustomTypography
                          id="validate-diagnosis"
                          color="error"
                          variant="body3"
                          className="pt-8 text-align-left"
                        >
                          {errors.partnerInfo.assignedTo}
                        </CustomTypography>
                      )}
                  </Grid2>
                </Grid2>
              </Box>
              <Box className="pt-32">
                <CustomDivider />
              </Box>
              <Box className="pt-32">
                <Grid2 container spacing={4} className="width-80">
                  <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                    <CustomTypography
                      variant="h5"
                      className="text-align-left pb-32"
                      id="facility-info-text"
                    >
                      Key Phone Numbers and Email
                    </CustomTypography>
                  </Grid2>
                </Grid2>
                <Grid2 container spacing={4} className="width-80">
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="primary-phone-label"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left pb-12"
                    >
                      Primary Phone
                    </CustomTypography>
                    <CustomTextField
                      id="txt-primary-phone"
                      placeholder="Type Primary Phone"
                      size="small"
                      value={values.contactDetails.primaryPhone}
                      onChange={(event) => {
                        const formatted = formatPhoneNumber(event.target.value);
                        setFieldValue('contactDetails.primaryPhone', formatted);
                      }}
                      onBlur={(event) => {
                        const formatted = formatPhoneNumber(event.target.value);
                        setFieldValue('contactDetails.primaryPhone', formatted);
                      }}
                    />
                    {errors?.contactDetails?.primaryPhone && (
                      <CustomTypography
                        id="validate-diagnosis"
                        color="error"
                        variant="body3"
                        className="pt-8 text-align-left"
                      >
                        {errors.contactDetails.primaryPhone}
                      </CustomTypography>
                    )}
                  </Grid2>

                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="IT-help-desk-phone-label"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left pb-12"
                    >
                      IT Help Desk Phone
                    </CustomTypography>
                    <CustomTextField
                      id="txt-IT-help-desk-phone"
                      placeholder="Type IT Help Desk Phone"
                      size="small"
                      value={values.contactDetails.itHelpDeskPhone}
                      onChange={(event) => {
                        const formatted = formatPhoneNumber(event.target.value);
                        setFieldValue(
                          'contactDetails.itHelpDeskPhone',
                          formatted
                        );
                      }}
                      onBlur={(event) => {
                        const formatted = formatPhoneNumber(event.target.value);
                        setFieldValue(
                          'contactDetails.itHelpDeskPhone',
                          formatted
                        );
                      }}
                    ></CustomTextField>
                    {errors?.contactDetails?.itHelpDeskPhone && (
                      <CustomTypography
                        id="validate-diagnosis"
                        color="error"
                        variant="body3"
                        className="pt-8 text-align-left"
                      >
                        {errors.contactDetails.itHelpDeskPhone}
                      </CustomTypography>
                    )}
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="IT-help-desk-label"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left pb-12"
                    >
                      IT Help Desk Email
                    </CustomTypography>
                    <CustomTextField
                      id="txt-IT-help-desk-email"
                      placeholder="Type IT Help Desk Email"
                      size="small"
                      value={values.contactDetails.itHelpDeskEmail}
                      onChange={(event) => {
                        setFieldValue(
                          'contactDetails.itHelpDeskEmail',
                          event.target.value
                        );
                      }}
                      onBlur={handleBlur}
                    />
                    {errors?.contactDetails?.itHelpDeskEmail && (
                      <CustomTypography
                        id="validate-diagnosis"
                        color="error"
                        variant="body3"
                        className="pt-8 text-align-left"
                      >
                        {errors.contactDetails.itHelpDeskEmail}
                      </CustomTypography>
                    )}
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="contact-email-label"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left pb-12"
                    >
                      Contact Email
                    </CustomTypography>
                    <CustomTextField
                      id="txt-contact-email"
                      placeholder="Type Contact Email"
                      size="small"
                      value={values.contactDetails.contactEmail}
                      onChange={(event) => {
                        setFieldValue(
                          'contactDetails.contactEmail',
                          event.target.value
                        );
                      }}
                      onBlur={handleBlur}
                    ></CustomTextField>
                    {errors?.contactDetails?.contactEmail && (
                      <CustomTypography
                        id="validate-diagnosis"
                        color="error"
                        variant="body3"
                        className="pt-8 text-align-left"
                      >
                        {errors.contactDetails.contactEmail}
                      </CustomTypography>
                    )}
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="fax-label"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left pb-12"
                    >
                      Fax
                    </CustomTypography>
                    <CustomTextField
                      id="txt-fax"
                      placeholder="Type Fax"
                      size="small"
                      value={values.contactDetails.fax}
                      onChange={(event) => {
                        const formatted = formatPhoneNumber(event.target.value);
                        setFieldValue('contactDetails.fax', formatted);
                      }}
                      onBlur={(event) => {
                        const formatted = formatPhoneNumber(event.target.value);
                        setFieldValue('contactDetails.fax', formatted);
                      }}
                    ></CustomTextField>
                    {errors?.contactDetails?.fax && (
                      <CustomTypography
                        id="validate-diagnosis"
                        color="error"
                        variant="body3"
                        className="pt-8 text-align-left"
                      >
                        {errors.contactDetails.fax}
                      </CustomTypography>
                    )}
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="other-email-label"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left pb-12"
                    >
                      Other Email
                    </CustomTypography>
                    <CustomTextField
                      id="txt-other-email"
                      placeholder="Type Other Email"
                      size="small"
                      value={values.contactDetails.otherEmail}
                      onChange={(event) => {
                        setFieldValue(
                          'contactDetails.otherEmail',
                          event.target.value
                        );
                      }}
                      onBlur={handleBlur}
                    ></CustomTextField>
                    {errors?.contactDetails?.otherEmail && (
                      <CustomTypography
                        id="validate-diagnosis"
                        color="error"
                        variant="body3"
                        className="pt-8 text-align-left"
                      >
                        {errors.contactDetails.otherEmail}
                      </CustomTypography>
                    )}
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}>
                    <CustomTypography
                      id="fax-description-label"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left pb-12"
                    >
                      Fax Description
                    </CustomTypography>
                    <CustomTextField
                      id="txt-fax-description"
                      placeholder="Type Fax Description"
                      size="small"
                      rows={4}
                      multiLine
                      value={values.contactDetails.faxDescription}
                      onChange={(event) => {
                        setFieldValue(
                          'contactDetails.faxDescription',
                          event.target.value
                        );
                      }}
                      onBlur={handleBlur}
                    ></CustomTextField>
                    {errors.contactDetails?.faxDescription && (
                      <CustomTypography
                        id="validate-fax-description"
                        color="error"
                        variant="body3"
                        className="pt-8 text-align-left"
                      >
                        {errors.contactDetails.faxDescription}
                      </CustomTypography>
                    )}
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}>
                    <CustomTypography
                      id="other-email-label"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left pb-12"
                    >
                      Other Email Description
                    </CustomTypography>
                    <CustomTextField
                      id="txt-other-email-description"
                      placeholder="Type Other Email Description"
                      size="small"
                      rows={4}
                      multiLine
                      value={values.contactDetails.otherEmailDescription}
                      onChange={(event) => {
                        setFieldValue(
                          'contactDetails.otherEmailDescription',
                          event.target.value
                        );
                      }}
                    ></CustomTextField>
                    {errors.contactDetails?.otherEmailDescription && (
                      <CustomTypography
                        id="validate-fax-description"
                        color="error"
                        variant="body3"
                        className="pt-8 text-align-left"
                      >
                        {errors.contactDetails.otherEmailDescription}
                      </CustomTypography>
                    )}
                  </Grid2>
                </Grid2>
              </Box>
              <Box className="pt-32">
                <CustomDivider />
              </Box>
              <Box className="pt-32">
                <Grid2 container spacing={4} className="width-80">
                  <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                    <CustomTypography
                      variant="h5"
                      className="text-align-left pb-32"
                      id="it-system-text"
                    >
                      IT System Details
                    </CustomTypography>
                  </Grid2>
                </Grid2>
                <Grid2 container spacing={4} className="width-80">
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="network-access-label"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left pb-12"
                    >
                      Network Access Type
                    </CustomTypography>
                    <CustomTextField
                      id="txt-network-access-type"
                      placeholder="Type Network Access"
                      size="small"
                      value={values.itSystemDetails.networkAccessType}
                      onChange={(event) => {
                        setFieldValue(
                          'itSystemDetails.networkAccessType',
                          event.target.value
                        );
                      }}
                    ></CustomTextField>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="emr-label"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left pb-12"
                    >
                      EMR
                    </CustomTypography>
                    <CustomTextField
                      id="txt-emr"
                      placeholder="Type EMR"
                      size="small"
                      value={values.itSystemDetails.emr}
                      onChange={(event) => {
                        setFieldValue(
                          'itSystemDetails.emr',
                          event.target.value
                        );
                      }}
                    ></CustomTextField>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="email-domain-primary-label"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left pb-12"
                    >
                      Email Domain Primary
                    </CustomTypography>
                    <CustomTextField
                      id="txt-email-domain-primary"
                      placeholder="Type Email Domain Primary"
                      size="small"
                      value={values.itSystemDetails.emailDomainPrimary}
                      onChange={(event) => {
                        setFieldValue(
                          'itSystemDetails.emailDomainPrimary',
                          event.target.value
                        );
                      }}
                    ></CustomTextField>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="network-access-label"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left pb-12"
                    >
                      Network Access URL
                    </CustomTypography>
                    <CustomTextField
                      id="txt-network-access-url"
                      placeholder="Type Network Access URL"
                      size="small"
                      value={values.itSystemDetails.networkAccessUrl}
                      onChange={(event) => {
                        setFieldValue(
                          'itSystemDetails.networkAccessUrl',
                          event.target.value
                        );
                      }}
                    ></CustomTextField>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="documentation-in-label"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left pb-12"
                    >
                      Documentation In
                    </CustomTypography>
                    <CustomTextField
                      id="txt-documentation-in"
                      placeholder="Type Documentation In"
                      size="small"
                      value={values.itSystemDetails.documentationIn}
                      onChange={(event) => {
                        setFieldValue(
                          'itSystemDetails.documentationIn',
                          event.target.value
                        );
                      }}
                    ></CustomTextField>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="email-domain-secondary-label"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left pb-12"
                    >
                      Email Domain Secondary
                    </CustomTypography>
                    <CustomTextField
                      id="txt-email-domain-secondary"
                      placeholder="Type Telemedicine Platform URL"
                      size="small"
                      value={values.itSystemDetails.emailDomainSecondary}
                      onChange={(event) => {
                        setFieldValue(
                          'itSystemDetails.emailDomainSecondary',
                          event.target.value
                        );
                      }}
                    ></CustomTextField>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="vpn-gateway-software-label"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left pb-12"
                    >
                      VPN Gateway Software
                    </CustomTypography>
                    <CustomTextField
                      id="txt-vpn-gateway-software"
                      placeholder="Type VPN Gateway Software"
                      size="small"
                      value={values.itSystemDetails.vpnGatewaySoftware}
                      onChange={(event) => {
                        setFieldValue(
                          'itSystemDetails.vpnGatewaySoftware',
                          event.target.value
                        );
                      }}
                    ></CustomTextField>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="telemedicine-platform-label"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left pb-12"
                    >
                      Telemedicine Platform
                    </CustomTypography>
                    <CustomTextField
                      id="txt-telemedicine-platform"
                      placeholder="Type Telemedicine Platform"
                      size="small"
                      value={values.itSystemDetails.telemedicinePlatform}
                      onChange={(event) => {
                        setFieldValue(
                          'itSystemDetails.telemedicinePlatform',
                          event.target.value
                        );
                      }}
                    ></CustomTextField>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="email-domain-tertiary-label"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left pb-12"
                    >
                      Email Domain Tertiary
                    </CustomTypography>
                    <CustomTextField
                      id="txt-email-domain-tertiary"
                      placeholder="Type Email Domain Tertiary"
                      size="small"
                      value={values.itSystemDetails.emailDomainTertiary}
                      onChange={(event) => {
                        setFieldValue(
                          'itSystemDetails.emailDomainTertiary',
                          event.target.value
                        );
                      }}
                    ></CustomTextField>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="mfa-label"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left pb-12"
                    >
                      MFA
                    </CustomTypography>
                    <CustomTextField
                      id="txt-mfa"
                      placeholder="Type MFA"
                      size="small"
                      value={values.itSystemDetails.mfa}
                      onChange={(event) => {
                        setFieldValue(
                          'itSystemDetails.mfa',
                          event.target.value
                        );
                      }}
                    ></CustomTextField>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="telemedicine-platform-url-label"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left pb-12"
                    >
                      Telemedicine Platform URL
                    </CustomTypography>
                    <CustomTextField
                      id="txt-telemedicine-platform-url"
                      placeholder="Type Telemedicine Platform URL"
                      size="small"
                      value={values.itSystemDetails.telemedicinePlatformUrl}
                      onChange={(event) => {
                        setFieldValue(
                          'itSystemDetails.telemedicinePlatformUrl',
                          event.target.value
                        );
                      }}
                    ></CustomTextField>
                  </Grid2>

                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="training-equipment-label"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left pb-12"
                    >
                      Training Requirement
                    </CustomTypography>
                    <CustomTextField
                      id="txt-training-equipment"
                      placeholder="Type Training Requirement"
                      size="small"
                      value={values.itSystemDetails.trainingEquipment}
                      onChange={(event) => {
                        setFieldValue(
                          'itSystemDetails.trainingEquipment',
                          event.target.value
                        );
                      }}
                    ></CustomTextField>
                  </Grid2>

                  <Grid2 size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}>
                    <CustomTypography
                      id="devices-utilized-label"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left pb-12"
                    >
                      Devices Utilized
                    </CustomTypography>
                    <CustomTextField
                      id="txt-devices-utilized"
                      placeholder="Type Devices Utilized"
                      size="small"
                      multiLine
                      rows={4}
                      value={values.itSystemDetails.devicesUtilized}
                      onChange={(event) => {
                        setFieldValue(
                          'itSystemDetails.devicesUtilized',
                          event.target.value
                        );
                      }}
                    ></CustomTextField>
                    {errors.itSystemDetails?.devicesUtilized && (
                      <CustomTypography
                        id="validate-devices-utilized"
                        color="error"
                        variant="body3"
                        className="pt-8 text-align-left"
                      >
                        {errors.itSystemDetails?.devicesUtilized}
                      </CustomTypography>
                    )}
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}>
                    <CustomTypography
                      id="it-notes-label"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left pb-12"
                    >
                      IT Notes
                    </CustomTypography>
                    <CustomTextField
                      id="txt-it-notes"
                      placeholder="Type IT Notes"
                      size="small"
                      multiLine
                      rows={4}
                      value={values.itSystemDetails.itNotes}
                      onChange={(event) => {
                        setFieldValue(
                          'itSystemDetails.itNotes',
                          event.target.value
                        );
                      }}
                    ></CustomTextField>
                    {errors.itSystemDetails?.itNotes && (
                      <CustomTypography
                        id="validate-devices-utilized"
                        color="error"
                        variant="body3"
                        className="pt-8 text-align-left"
                      >
                        {errors.itSystemDetails?.itNotes}
                      </CustomTypography>
                    )}
                  </Grid2>
                </Grid2>
              </Box>
              <Box className="pt-32">
                <CustomDivider />
              </Box>
              <Box className="pt-32">
                <Grid2 container spacing={4} className="width-80">
                  <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                    <CustomTypography
                      variant="h5"
                      className="text-align-left pb-32"
                      id="address-details-text"
                    >
                      Address Details
                    </CustomTypography>
                  </Grid2>
                </Grid2>
                <Grid2 container spacing={4} className="width-80">
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="physical-address-line-1-label"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left pb-12"
                    >
                      Address Line 1
                    </CustomTypography>
                    <CustomTextField
                      id="txt-physical-address-line-1"
                      placeholder="Type Address Line 1"
                      size="small"
                      value={values.address.addressLine1}
                      onChange={(event) => {
                        setFieldValue(
                          'address.addressLine1',
                          event.target.value
                        );
                      }}
                    ></CustomTextField>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="physical-city-label"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left pb-12"
                    >
                      City
                    </CustomTypography>
                    <CustomTextField
                      id="txt-physical-city"
                      placeholder="Type City"
                      size="small"
                      value={values.address.city}
                      onChange={(event) => {
                        setFieldValue('address.city', event.target.value);
                      }}
                    ></CustomTextField>
                  </Grid2>

                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="physical-postal-code-label"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left pb-12"
                    >
                      Postal Code
                    </CustomTypography>
                    <CustomTextField
                      id="txt-physical-postal-code"
                      placeholder="Type Postal Code"
                      size="small"
                      // type="number"
                      value={values.address.postalCode}
                      onChange={(event) => {
                        const formatted = validatePostalCode(
                          event.target.value
                        );
                        setFieldValue('address.postalCode', formatted);
                      }}
                    ></CustomTextField>
                    {errors.address?.postalCode &&
                      touched.address?.postalCode && (
                        <CustomTypography
                          id="partner-physical-postal-code-error"
                          variant="body3"
                          color="error"
                          className="text-align-left"
                        >
                          {errors.address.postalCode}
                        </CustomTypography>
                      )}
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="physical-address-line-2-label"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left pb-12"
                    >
                      Address Line 2
                    </CustomTypography>
                    <CustomTextField
                      id="txt-physical-address-line-2"
                      placeholder="Type Address Line 2"
                      size="small"
                      value={values.address.addressLine2}
                      onChange={(event) => {
                        setFieldValue(
                          'address.addressLine2',
                          event.target.value
                        );
                      }}
                    ></CustomTextField>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="physical-state-label"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left pb-12"
                    >
                      State
                    </CustomTypography>
                    <CustomSelect
                      id="select-physical-state"
                      placeholder="Select State"
                      size="small"
                      onChange={(value) => {
                        setFieldValue('address.state', value);
                      }}
                      selectedValue={values.address.state}
                      options={mapUsStatesToOptions(PartnerMetaData?.usStates)}
                      fullWidth
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="physical-country-label"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left pb-12"
                    >
                      County
                    </CustomTypography>
                    <CustomTextField
                      id="txt-physical-country"
                      placeholder="Type County"
                      size="small"
                      value={values.address.county}
                      onChange={(event) => {
                        setFieldValue('address.county', event.target.value);
                      }}
                    ></CustomTextField>
                  </Grid2>
                </Grid2>
              </Box>
              <Box className="pt-32">
                <CustomDivider />
              </Box>
              <Box className="pt-32">
                <Grid2 container spacing={4} className="width-80">
                  <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                    <CustomTypography
                      variant="h5"
                      className="text-align-left pb-32"
                      id="description-details-text"
                    >
                      Additional Details
                    </CustomTypography>
                  </Grid2>
                </Grid2>
                <Grid2 container spacing={4} className="width-80">
                  <Grid2 size={{ xs: 12, sm: 12, md: 8, lg: 8, xl: 8 }}>
                    <CustomTypography
                      id="description-label"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left pb-12"
                    >
                      Description
                    </CustomTypography>
                    <CustomTextField
                      id="txt-description"
                      placeholder="Type Description"
                      size="small"
                      multiLine
                      rows={4}
                      value={values.description}
                      onChange={(event) => {
                        setFieldValue('description', event.target.value);
                      }}
                    ></CustomTextField>
                    {errors.description && (
                      <CustomTypography
                        id="validate-fax-description"
                        color="error"
                        variant="body3"
                        className="pt-8 text-align-left"
                      >
                        {errors.description}
                      </CustomTypography>
                    )}
                  </Grid2>
                </Grid2>
              </Box>
              <Box className="pt-32">
                <Grid2 container>
                  <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                    <Box className="display-flex justify-end">
                      <Box className="pr-12">
                        <CustomButton
                          id="cancel-button"
                          onClick={() => {
                            handleOpenPartnerConfirmDialog(true);
                          }}
                          variant="outlined"
                        >
                          Cancel
                        </CustomButton>
                      </Box>
                      <Box>
                        <CustomButton
                          id="save-button"
                          onClick={() => {
                            setPartnerSubmitAction(
                              partnerId ? 'Update' : 'Save'
                            );
                            handleSubmit();
                          }}
                          variant="contained"
                          // disabled={!isValid || !dirty}
                        >
                          {partnerId ? 'Update' : 'Save'}
                        </CustomButton>
                      </Box>
                    </Box>
                  </Grid2>
                </Grid2>
              </Box>
            </Box>
          </Form>
        )}
      </Formik>
      <Box>
        <Box>
          <ConfirmDiscardDialog
            open={openPartnerConfirmDialog}
            onClose={() => handleOpenPartnerConfirmDialog(false)}
            navigateTo={
              partnerId && !location.state?.isList
                ? `${VIEW_PARTNER_ROUTE_PATH}/${partnerId}`
                : PARTNER_ROUTE_PATH
            }
          />
        </Box>
      </Box>
    </Box>
  );
};

export default AddNewPartner;
